/**
 * ConcoursBf — Content service (frontend)
 *
 * ABSTRACTION LAYER for courses, quizzes, resources and simulations.
 *
 * Supports dual modes via REACT_APP_CONTENT_SOURCE:
 * - 'api' (default): calls backend Express API (PostgreSQL data)
 * - 'json': reads directly from local V8 JSON files
 *
 * NO silent fallback: errors are explicitly propagated.
 */

import apiClient from './apiClient';
import { readOfflinePremium } from './offlinePremiumService';

// JSON content is intentionally loaded lazily.
// In normal API mode these large files are not part of the initial JS bundle.
const jsonLoaders = {
  quizzes: {
    Culture_General: () => import('../data/subsistances/culture_generale_actualite/quizzes.json'),
    histoire_Burkina_Faso: () => import('../data/subsistances/histoire_geographie_burkina/quizzes.json'),
    histoire_generale: () => import('../data/subsistances/histoire_generale/quizzes.json'),
    litteratur: () => import('../data/subsistances/francais_litterature/quizzes.json'),
    math: () => import('../data/subsistances/mathematiques/quizzes.json'),
    physique_chimi: () => import('../data/subsistances/physique_chimie/quizzes.json'),
    psychotechnique: () => import('../data/subsistances/psychotechnique_logique/quizzes.json'),
    quelq_specialité: () => import('../data/subsistances/specialites/quizzes.json'),
    svt: () => import('../data/subsistances/svt_sante/quizzes.json'),
  },
  courses: {
    Culture_General: () => import('../data/subsistances/culture_generale_actualite/cours.json'),
    histoire_Burkina_Faso: () => import('../data/subsistances/histoire_geographie_burkina/cours.json'),
    histoire_generale: () => import('../data/subsistances/histoire_generale/cours.json'),
    litteratur: () => import('../data/subsistances/francais_litterature/cours.json'),
    math: () => import('../data/subsistances/mathematiques/cours.json'),
    physique_chimi: () => import('../data/subsistances/physique_chimie/cours.json'),
    psychotechnique: () => import('../data/subsistances/psychotechnique_logique/cours.json'),
    quelq_specialité: () => import('../data/subsistances/specialites/cours.json'),
    svt: () => import('../data/subsistances/svt_sante/cours.json'),
  },
  resources: () => import('../data/resources.json'),
  simulations: () => import('../data/simulations.json'),
};

const JSON_ALIASES = {
  culture_generale_actualite: 'Culture_General',
  histoire_geographie_burkina: 'histoire_Burkina_Faso',
  francais_litterature: 'litteratur',
  mathematiques: 'math',
  physique_chimie: 'physique_chimi',
  psychotechnique_logique: 'psychotechnique',
  specialites: 'quelq_specialité',
  svt_sante: 'svt',
};

async function loadJson(loader) {
  const module = await loader();
  return module.default ?? module;
}

function resolveJsonKey(key) {
  return JSON_ALIASES[key] || key;
}

function normalizeJsonQuiz(data) {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    module_slug:
      data.module_slug ||
      data.metadata?.slug ||
      null,
  };
}

function withContentAliases(data) {
  const result = { ...(data || {}) };

  for (const [modernKey, legacyKey] of Object.entries(JSON_ALIASES)) {
    if (result[modernKey] && !result[legacyKey]) {
      result[legacyKey] = result[modernKey];
    } else if (result[legacyKey] && !result[modernKey]) {
      result[modernKey] = result[legacyKey];
    }
  }

  return result;
}

const RESOURCE_CATEGORIES = {
  Culture_General: { titre: 'Culture Générale', icon: '🌍' },
  histoire_Burkina_Faso: { titre: 'Histoire du Burkina Faso', icon: '🏛️' },
  histoire_generale: { titre: 'Histoire Générale', icon: '📜' },
  litteratur: { titre: 'Littérature', icon: '📚' },
  math: { titre: 'Mathématiques', icon: '📐' },
  physique_chimi: { titre: 'Physique-Chimie', icon: '⚗️' },
  psychotechnique: { titre: 'Psychologie', icon: '🧠' },
  quelq_specialité: { titre: 'Spécialité', icon: '🎯' },
  svt: { titre: 'SVT', icon: '🧬' },
};

// Content source toggle ('api' | 'json').
// Direct JSON access is a local-development/testing aid only. A production
// build must always pass through the API so Premium payload filtering cannot
// be bypassed by a frontend environment variable.
const REQUESTED_CONTENT_SOURCE =
  process.env.REACT_APP_CONTENT_SOURCE || 'api';

export const CONTENT_SOURCE =
  process.env.NODE_ENV === 'production'
    ? 'api'
    : REQUESTED_CONTENT_SOURCE === 'json'
    ? 'json'
    : 'api';

// ── Public Async API ──

/**
 * getQuizData(subjectKey) → quiz object for a given subject
 */
export async function getQuizData(subjectKey) {
  if (CONTENT_SOURCE === 'json') {
    const key = resolveJsonKey(subjectKey);
    const loader = jsonLoaders.quizzes[key];
    return loader ? normalizeJsonQuiz(await loadJson(loader)) : null;
  }
  try {
    return await apiClient(`/content/quizzes/${encodeURIComponent(subjectKey)}`);
  } catch (error) {
    if (error?.status) throw error;
    const offline = await offlineQuizMapping();
    return offline[subjectKey] || null;
  }
}

/**
 * getAllQuizData() → full mapping of subject → quiz data
 */
export async function getAllQuizData() {
  if (CONTENT_SOURCE === 'json') {
    const entries = await Promise.all(
      Object.entries(jsonLoaders.quizzes).map(async ([key, loader]) => [
        key,
        normalizeJsonQuiz(await loadJson(loader)),
      ])
    );
    const result = Object.fromEntries(entries);
    for (const [alias, canonical] of Object.entries(JSON_ALIASES)) {
      result[alias] = result[canonical];
    }
    return result;
  }
  try {
    return withContentAliases(await apiClient('/content/quizzes'));
  } catch (error) {
    if (error?.status) throw error;
    return withContentAliases(await offlineQuizMapping());
  }
}

/**
 * getCoursData(subjectKey) → course object for a given subject
 */
export async function getCoursData(subjectKey) {
  if (CONTENT_SOURCE === 'json') {
    const key = resolveJsonKey(subjectKey);
    const loader = jsonLoaders.courses[key];
    return loader ? loadJson(loader) : null;
  }
  try {
    return await apiClient(`/content/courses/${encodeURIComponent(subjectKey)}`);
  } catch (error) {
    if (error?.status) throw error;
    const courses = await offlineCourseMapping();
    return courses[subjectKey] || null;
  }
}

/**
 * getAllCoursData() → full mapping of subject → course data
 */
export async function getAllCoursData() {
  if (CONTENT_SOURCE === 'json') {
    const entries = await Promise.all(
      Object.entries(jsonLoaders.courses).map(async ([key, loader]) => [key, await loadJson(loader)])
    );
    const result = Object.fromEntries(entries);
    for (const [alias, canonical] of Object.entries(JSON_ALIASES)) {
      result[alias] = result[canonical];
    }
    return result;
  }
  try {
    return withContentAliases(await apiClient('/content/courses'));
  } catch (error) {
    if (error?.status) throw error;
    return withContentAliases(await offlineCourseMapping());
  }
}

/**
 * getResourcesData() → resources structured mapping
 */
export async function getResourcesData() {
  if (CONTENT_SOURCE === 'json') {
    return loadJson(jsonLoaders.resources);
  }

  // Modules are created and deactivated in the back-office. The mobile/web
  // catalogue must read that same active set, never a frozen nine-item list.
  let apiModules, apiResources;
  try {
    [apiModules, apiResources] = await Promise.all([
      apiClient('/content/modules'),
      apiClient('/content/resources'),
    ]);
  } catch (error) {
    if (error?.status) throw error;
    const offline = await readOfflinePremium();
    apiResources = (offline.resources || []).map((resource) => {
      const file = resource.offlineFile;
      return file?.base64 ? {
        ...resource,
        pdfUrl: `data:${file.mimeType || 'application/octet-stream'};base64,${file.base64}`,
        url: `data:${file.mimeType || 'application/octet-stream'};base64,${file.base64}`,
        locked: false,
      } : { ...resource, locked: false };
    });
    const modules = new Map();
    for (const course of offline.courses || []) {
      if (course.module_slug) modules.set(course.module_slug, {
        slug: course.module_slug, matiere: course.matiere || course.module_slug,
      });
    }
    for (const resource of apiResources) {
      if (resource.module_slug) modules.set(resource.module_slug, {
        slug: resource.module_slug,
        matiere: resource.module_title || resource.module_slug,
      });
    }
    apiModules = [...modules.values()];
  }
  const mapping = {};
  for (const module of apiModules || []) {
    if (module.is_active === false || !module.slug) continue;
    const key = JSON_ALIASES[module.slug] || module.slug;
    const legacy = RESOURCE_CATEGORIES[key];
    mapping[key] = {
      titre: module.matiere || legacy?.titre || module.slug,
      icon: legacy?.icon || '📚',
      description: module.description || '',
      access_tier: 'free',
      locked: false,
      ressources: [],
    };
  }

  for (const resource of apiResources || []) {
    if (!resource.module_slug) continue;
    const key = JSON_ALIASES[resource.module_slug] || resource.module_slug;
    if (!mapping[key]) continue; // inactive/unpublished module
    mapping[key].ressources.push({
      id: resource.id,
      source_key: resource.source_key,
      type: resource.type || 'document',
      titre: resource.titre || resource.title,
      description: resource.description || '',
      duree:
        resource.duree ||
        (Number.isInteger(resource.duration_minutes)
          ? `${resource.duration_minutes} min`
          : ''),
      niveau: resource.niveau || 'base',
      contenu: resource.contenu || '',
      pdfUrl: resource.pdfUrl || resource.url || null,
      url: resource.url || null,
      tags: Array.isArray(resource.tags) ? resource.tags : [],
      access_tier: resource.access_tier || 'free',
      locked: Boolean(resource.locked),
    });
  }

  return mapping;
}

/**
 * Normalize simulation content to the workflow used by the UI:
 * Simulations -> Concours directs / Concours professionnels -> Lots -> Sujets.
 *
 * Product rule:
 * - a lot is a neutral numbered container (Lot 1, Lot 2, ...);
 * - lots are NOT categories for a target competition;
 * - the future back-office controls how many lots exist and how many subjects
 *   belong to each lot.
 *
 * The current PostgreSQL/JSON data predates this workflow. During this
 * transition we flatten all legacy direct simulations into one neutral demo
 * lot instead of turning old competition categories into product lots.
 */
export function normalizeSimulationWorkflow(rawData) {
  if (
    rawData &&
    !Array.isArray(rawData) &&
    rawData.direct?.lots &&
    rawData.professional?.lots
  ) {
    return rawData;
  }

  if (
    Array.isArray(rawData) &&
    rawData.some((simulation) => simulation?.lot_id || simulation?.lot_slug)
  ) {
    const workflows = {
      direct: {
        key: 'direct',
        titre: 'Concours directs',
        description: 'Sujets de concours organisés en lots neutres.',
        lots: [],
      },
      professional: {
        key: 'professional',
        titre: 'Concours professionnels',
        description: 'Sujets de concours professionnels.',
        lots: [],
      },
    };

    const lotsByType = {
      direct: new Map(),
      professional: new Map(),
    };

    for (const simulation of rawData) {
      const competitionType =
        simulation.competition_type === 'professional'
          ? 'professional'
          : 'direct';
      const lotKey =
        simulation.lot_slug ||
        (simulation.lot_id ? `lot-${simulation.lot_id}` : 'lot-1');

      if (!lotsByType[competitionType].has(lotKey)) {
        lotsByType[competitionType].set(lotKey, {
          key: lotKey,
          id: simulation.lot_id || null,
          titre: simulation.lot_titre || 'Lot',
          ordre: Number(simulation.lot_order) || 0,
          sujets: [],
        });
      }

      const epreuve = simulation.epreuves?.[0] || {};
      lotsByType[competitionType].get(lotKey).sujets.push({
        key: simulation.slug || `simulation-${simulation.id}`,
        id: simulation.id,
        titre: simulation.titre || epreuve.titre || 'Sujet',
        description: simulation.description || '',
        nombre_questions:
          epreuve.nombre_questions ?? epreuve.questions?.length ?? 0,
        duree_minutes: simulation.duree_minutes ?? epreuve.duree_minutes ?? null,
        questions: epreuve.questions || [],
        simulation_id: simulation.id,
        access_tier: simulation.access_tier || 'free',
        locked: Boolean(simulation.locked),
        display_order: Number(simulation.display_order) || 0,
      });
    }

    for (const type of ['direct', 'professional']) {
      workflows[type].lots = Array.from(lotsByType[type].values())
        .sort((a, b) => a.ordre - b.ordre)
        .map((lot) => ({
          ...lot,
          sujets: lot.sujets.sort(
            (a, b) => a.display_order - b.display_order
          ),
        }));
    }

    workflows.professional.is_ready = workflows.professional.lots.length > 0;
    return workflows;
  }

  const legacySubjects = [];

  const makeLegacySubject = (subject, context = {}) => ({
    key: context.key,
    titre: subject.titre || context.source_title || 'Sujet',
    source_title: subject.titre || context.source_title || '',
    description: subject.description || '',
    nombre_questions: subject.nombre_questions ?? subject.questions?.length ?? 0,
    duree_minutes: subject.duree_minutes ?? context.duree_minutes ?? null,
    questions: subject.questions || [],
    simulation_id: context.simulation_id ?? subject.simulation_id ?? subject.id ?? null,
    seuil_admission: subject.seuil_admission ?? context.seuil_admission ?? null,
    quota_poste: subject.quota_poste ?? context.quota_poste ?? null,
    est_sujets_officiels:
      subject.est_sujets_officiels ?? context.est_sujets_officiels ?? false,
    source_epreuve_key: context.source_epreuve_key || null,
    source_lot_key: context.source_lot_key || null,
    source_sujet_key: context.source_sujet_key || null,
    access_tier: subject.access_tier || context.access_tier || 'free',
    locked: Boolean(subject.locked ?? context.locked),
  });

  const collectLegacyExam = (categoryKey, examKey, exam) => {
    if (!exam || typeof exam !== 'object') return;

    const baseContext = {
      simulation_id: exam.id ?? null,
      seuil_admission: exam.seuil_admission ?? null,
      quota_poste: exam.quota_poste ?? null,
      duree_minutes: exam.duree_minutes ?? null,
      est_sujets_officiels:
        exam.est_sujets_officiels || categoryKey === 'sujets_officiels_bf',
      access_tier: exam.access_tier || 'free',
      locked: Boolean(exam.locked),
    };

    if (Array.isArray(exam.lots)) {
      exam.lots.forEach((legacyLot, lotIndex) => {
        (legacyLot.sujets || []).forEach((subject, subjectIndex) => {
          legacySubjects.push(
            makeLegacySubject(subject, {
              ...baseContext,
              key: `${categoryKey}_${examKey}_lot_${lotIndex + 1}_sujet_${subjectIndex + 1}`,
              source_title: subject.titre,
              source_lot_key: legacyLot.key || null,
              source_sujet_key: subject.key || null,
            })
          );
        });
      });
    }

    if (Array.isArray(exam.epreuves)) {
      exam.epreuves.forEach((epreuve, index) => {
        legacySubjects.push(
          makeLegacySubject(epreuve, {
            ...baseContext,
            key: `${categoryKey}_${examKey}_epreuve_${index + 1}`,
            source_title: epreuve.titre,
            source_epreuve_key: epreuve.key || null,
          })
        );
      });
    }
  };

  if (Array.isArray(rawData)) {
    rawData.forEach((simulation, index) => {
      collectLegacyExam(
        'direct',
        simulation.key || `simulation_${simulation.id || index + 1}`,
        simulation
      );
    });
  } else if (rawData && typeof rawData === 'object') {
    Object.entries(rawData).forEach(([categoryKey, categoryValue]) => {
      if (!categoryValue || typeof categoryValue !== 'object') return;

      Object.entries(categoryValue).forEach(([examKey, exam]) => {
        collectLegacyExam(categoryKey, examKey, exam);
      });
    });
  }

  return {
    direct: {
      key: 'direct',
      titre: 'Concours directs',
      description: 'Sujets de concours organisés en lots neutres.',
      lots:
        legacySubjects.length > 0
          ? [
              {
                key: 'lot_1',
                ordre: 1,
                sujets: legacySubjects,
              },
            ]
          : [],
    },
    professional: {
      key: 'professional',
      titre: 'Concours professionnels',
      description: 'La structure de cette rubrique sera définie ultérieurement.',
      lots: [],
      is_ready: false,
    },
  };
}

/**
 * getSimulationsData() -> workflow normalized for the simulations UI.
 */
export async function getSimulationsData() {
  if (CONTENT_SOURCE === 'json') {
    return normalizeSimulationWorkflow(await loadJson(jsonLoaders.simulations));
  }

  try {
    const apiSims = await apiClient('/content/simulations');
    return normalizeSimulationWorkflow(apiSims);
  } catch (error) {
    if (error?.status) throw error;
    const offline = await readOfflinePremium();
    return normalizeSimulationWorkflow(
      (offline.simulations || []).map((simulation) => ({ ...simulation, locked: false }))
    );
  }
}

/**
 * Reconstruct only the Premium subjects from the signed native package.
 * Never store these answer keys in localStorage, Cache API or IndexedDB.
 */
async function offlineQuizMapping() {
  const offline = await readOfflinePremium();
  const modules = {};
  for (const subject of offline.quizzes || []) {
    if (!subject.module_slug || !subject.domaine_slug) continue;
    const mod = modules[subject.module_slug] || {
      module_slug: subject.module_slug,
      module_title: subject.module_title || subject.module_slug,
      domaines: {},
    };
    const domaine = mod.domaines[subject.domaine_slug] || {
      titre: subject.domaine_title || subject.domaine_slug,
      sujets: [],
    };
    domaine.sujets.push({ ...subject, locked: false });
    mod.domaines[subject.domaine_slug] = domaine;
    modules[subject.module_slug] = mod;
  }
  return modules;
}

async function offlineCourseMapping() {
  const offline = await readOfflinePremium();
  const modules = {};
  for (const course of offline.courses || []) {
    if (!course.module_slug) continue;
    const current = modules[course.module_slug] || {
      module_slug: course.module_slug,
      matiere: course.matiere || course.module_slug,
      courses: [],
    };
    current.courses.push({ ...course, locked: false });
    modules[course.module_slug] = current;
  }
  return modules;
}
